<template>
  <div class="dashboard">
    <!-- 统计卡片 -->
    <el-row :gutter="20" class="stats-row">
      <el-col :xs="24" :sm="12" :md="6">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-icon" style="background: #e3f2fd;">
              <el-icon :size="32" color="#1976d2"><Document /></el-icon>
            </div>
            <div class="stat-info">
              <div class="stat-value">{{ stats.total }}</div>
              <div class="stat-label">总识别次数</div>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="6">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-icon" style="background: #e8f5e9;">
              <el-icon :size="32" color="#388e3c"><SuccessFilled /></el-icon>
            </div>
            <div class="stat-info">
              <div class="stat-value">{{ stats.correct }}</div>
              <div class="stat-label">成功次数</div>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="6">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-icon" style="background: #fff3e0;">
              <el-icon :size="32" color="#f57c00"><Timer /></el-icon>
            </div>
            <div class="stat-info">
              <div class="stat-value">{{ stats.avgTime }}ms</div>
              <div class="stat-label">平均耗时</div>
            </div>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="6">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-icon" style="background: #fce4ec;">
              <el-icon :size="32" color="#c2185b"><TrendCharts /></el-icon>
            </div>
            <div class="stat-info">
              <div class="stat-value">{{ stats.successRate }}%</div>
              <div class="stat-label">成功率</div>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 介绍卡片（保持不变） -->
    <el-row :gutter="20" class="info-row">
      <el-col :xs="24" :md="8">
        <el-card class="info-card">
          <template #header>
            <div class="card-header">
              <el-icon><InfoFilled /></el-icon>
              <span>系统简介</span>
            </div>
          </template>
          <p>
            本系统基于深度学习模型，实现网站图形验证码的自动识别，支持在线单张识别演示与批量识别任务，
            通过前端 Vue + 后端 Spring Boot + Python 深度学习服务协同完成。
          </p>
        </el-card>
      </el-col>
      <el-col :xs="24" :md="8">
        <el-card class="info-card">
          <template #header>
            <div class="card-header">
              <el-icon><Tools /></el-icon>
              <span>技术栈</span>
            </div>
          </template>
          <ul>
            <li>前端：Vue 3.0 + Element Plus</li>
            <li>后端：Spring Boot + MyBatis</li>
            <li>模型服务：自训练的验证码识别模型 + Flask服务</li>
            <li>数据库：MySQL</li>
          </ul>
        </el-card>
      </el-col>
      <el-col :xs="24" :md="8">
        <el-card class="info-card">
          <template #header>
            <div class="card-header">
              <el-icon><Reading /></el-icon>
              <span>研究背景</span>
            </div>
          </template>
          <p>
            随着智慧城市和互联网业务的发展，验证码既是安全防线，又面临深度学习破解挑战。
            本项目从攻防视角出发，搭建一个完整的验证码识别系统原型，为后续安全防护策略研究提供支撑。
          </p>
        </el-card>
      </el-col>
    </el-row>

    <!-- 功能模块和使用说明（保持不变） -->
    <el-row :gutter="20" class="detail-row">
      <el-col :xs="24" :md="12">
        <el-card class="detail-card">
          <template #header>
            <div class="card-header">
              <el-icon><Menu /></el-icon>
              <span>系统功能模块</span>
            </div>
          </template>
          <ol>
            <li>验证码数据集构建与预处理（按文件名自动标注、数据增强）</li>
            <li>深度学习模型训练（CNN 网络、知识蒸馏、增量学习）</li>
            <li>在线识别服务（单张识别、批量任务、文件名比对模式）</li>
            <li>人工修正与数据闭环（标记正确/错误、提交修正值）</li>
            <li>增量训练自动触发（Redis 队列、异步训练、模型版本管理）</li>
            <li>模型性能分析（混淆矩阵、字符错误率、置信度分布）</li>
            <li>开放模型评测（上传第三方模型、自动生成评测报告）</li>
            <li>系统配置与统计展示（全局统计、历史记录、批次导出）</li>
          </ol>
        </el-card>
      </el-col>
      <el-col :xs="24" :md="12">
        <el-card class="detail-card">
          <template #header>
            <div class="card-header">
              <el-icon><Guide /></el-icon>
              <span>使用说明</span>
            </div>
          </template>
          <ol>
            <li>
              <strong>环境准备</strong>
              <ul>
                <li>安装 Python 3.8+，并安装依赖：<code>pip install torch torchvision pillow tqdm pandas</code>（推荐使用虚拟环境）。</li>
                <li>安装 Node.js 14+ 和 npm。</li>
                <li>安装 Java 8 和 Maven。</li>
                <li>安装 Redis 并启动（默认端口 6379）。</li>
                <li>安装 MySQL 5.7+，创建数据库 <code>captcha_db</code>，并初始化表结构。</li>
              </ul>
            </li>
            <li>
              <strong>配置文件修改</strong>
              <p>在 <code>captcha-backend/src/main/resources/application.yml</code> 中，根据实际情况修改以下配置：</p>
              <ul>
                <li>数据库连接（用户名、密码、URL）。</li>
                <li>Redis 连接（默认无需修改）。</li>
                <li><code>ml-service.val-dir</code>：设置为验证集绝对路径，例如 <code>F:/Desktop/LX/A_Learning/0_毕业论文/项目/train_code/dataset/val</code>。</li>
                <li><code>ml-service.evaluate-script</code>：指向开放评测脚本 <code>../captcha-ml-service/evaluate_uploaded_model.py</code>。</li>
                <li><code>ml-service.analysis-script</code>：指向系统分析脚本 <code>../captcha-ml-service/evaluate_model.py</code>（用于模型历史页面的分析）。</li>
              </ul>
            </li>
            <li>
              <strong>启动各服务（按顺序）</strong>
              <ol type="a">
                <li>启动 Redis 服务（如已安装为服务可跳过）。</li>
                <li>启动 MySQL 服务。</li>
                <li>启动 Python 模型服务：在 <code>captcha-ml-service</code> 目录下执行 <code>python main.py</code>。</li>
                <li>启动增量训练消费者（用于处理人工修正后的训练任务）：打开新终端，在 <code>captcha-ml-service</code> 目录下执行 <code>python incremental_train_consumer.py</code>。</li>
                <li>启动后端 Spring Boot 应用：在 <code>captcha-backend</code> 目录下使用 IDE 运行 <code>CaptchaBackendApplication</code>，或执行 Maven 命令 <code>mvn spring-boot:run</code>。</li>
                <li>启动前端 Vue 应用：在 <code>captcha-frontend</code> 目录下依次执行 <code>npm install</code>（仅首次）和 <code>npm run serve</code>。</li>
              </ol>
            </li>
            <li>
              <strong>访问系统</strong>
              <p>打开浏览器访问 <code>http://localhost:8081</code>（前端默认端口）。系统主要功能页面：</p>
              <ul>
                <li><strong>系统概览</strong>：展示全局统计数据，如总识别次数、成功率、平均耗时等。</li>
                <li><strong>在线识别</strong>：上传单张或多张验证码图片进行识别，支持文件名比对模式（自动填充修正值）。</li>
                <li><strong>历史记录</strong>：查看所有识别记录，可人工标记正确/错误、提交修正值。选中批次后可导出 CSV 结果。</li>
                <li><strong>模型历史</strong>：查看每次增量训练保存的模型版本，支持下载、应用回滚、分析模型性能（生成混淆矩阵、字符错误率等图表）。</li>
                <li><strong>模型评测</strong>（开放平台）：上传自己训练的模型（需与系统模型结构一致），在标准验证集上自动评测并返回详细性能报告。</li>
              </ul>
            </li>
            <li>
              <strong>注意事项</strong>
              <ul>
                <li>所有 Python 服务（predict.py 和 incremental_train_consumer.py）需保持后台运行，关闭终端即停止。</li>
                <li>增量训练消费者依赖 Redis 队列，请确保 Redis 已启动。</li>
                <li>开放评测功能会调用 <code>evaluate_uploaded_model.py</code>，该脚本需与系统其他 Python 脚本位于同一目录。</li>
                <li>首次使用时，训练集和验证集需按照文件名格式（标签_随机数.png）存放，确保数据集可被正确加载。</li>
              </ul>
            </li>
          </ol>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { getGlobalStats } from '@/api/captcha'   // 确保路径正确
import { ElMessage } from 'element-plus'
import {
  Document,
  SuccessFilled,
  Timer,
  TrendCharts,
  InfoFilled,
  Tools,
  Reading,
  Menu,
  Guide
} from '@element-plus/icons-vue'

const stats = ref({
  total: 0,
  correct: 0,
  incorrect: 0,
  avgTime: 0,          // 数字，用于显示 "xxx ms"
  successRate: 0        // 数字，用于显示 "xxx%"
})
const loading = ref(false)

const fetchStats = async () => {
  loading.value = true
  try {
    const resp = await getGlobalStats()
    // 假设 resp.data 结构为 { code:200, data: { total, correct, incorrect, avgCostTime, successRate } }
    if (resp.data.code === 200) {
      const data = resp.data.data
      // 从字符串中提取数字，例如 "12.34 ms" → 12.34, "80.00 %" → 80.00
      const avgTimeStr = data.avgCostTime || '0 ms'
      const successRateStr = data.successRate || '0 %'
      // 提取数字部分（支持整数或小数）
      const avgTimeNum = parseFloat(avgTimeStr) || 0
      const successRateNum = parseFloat(successRateStr) || 0

      stats.value = {
        total: data.total || 0,
        correct: data.correct || 0,
        incorrect: data.incorrect || 0,
        avgTime: avgTimeNum,
        successRate: successRateNum
      }
    } else {
      ElMessage.error(resp.data.msg || '获取统计数据失败')
    }
  } catch (e) {
    console.error('获取统计数据异常', e)
    ElMessage.error('请求统计接口失败')
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchStats()
})
</script>

<style scoped>
.dashboard {
  padding: 10px;
}

.stats-row {
  margin-bottom: 20px;
}

.stat-card {
  height: 100%;
}

.stat-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.stat-icon {
  width: 64px;
  height: 64px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-info {
  flex: 1;
}

.stat-value {
  font-size: 28px;
  font-weight: bold;
  color: #303133;
  line-height: 1.2;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 14px;
  color: #909399;
}

.info-row {
  margin-bottom: 20px;
}

.info-card {
  height: 100%;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
}

.info-card p {
  font-size: 14px;
  line-height: 1.6;
  color: #606266;
  margin: 0;
}

.info-card ul {
  list-style: disc;
  padding-left: 20px;
  font-size: 14px;
  color: #606266;
  line-height: 1.8;
  margin: 0;
}

.detail-row {
  margin-bottom: 20px;
}

/* 核心修改：固定高度并使内部滚动 */
.detail-card {
  height: 300px; /* 固定高度，可根据需要调整 */
  display: flex;
  flex-direction: column;
  overflow: hidden; /* 防止整个卡片溢出 */
}

.detail-card :deep(.el-card__body) {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

/* 确保列表容器占满剩余空间 */
.detail-card :deep(.el-card__body) > ol,
.detail-card :deep(.el-card__body) > ul {
  height: 100%;
  margin: 0;
  padding-left: 20px;
}

/* 原有的样式保留 */
.detail-card ol {
  padding-left: 20px;
  font-size: 14px;
  color: #606266;
  line-height: 1.8;
  margin: 0;
}

.detail-card li {
  margin-bottom: 8px;
}

.detail-card code {
  background: #f5f7fa;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: #e6a23c;
}

/* 全局卡片内边距（如不影响其他卡片可保留） */
:deep(.el-card__body) {
  padding: 20px;
}
</style>